'use client';

import useSWR from 'swr';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  fetchWithTracingAndLogging,
  shouldRetryOnServerError,
} from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isValidHotelId } from '@/app/components/hotel-detail/common/common.helper';

type GuestSummarizedReviewsResponse = {
  review: string | null;
};

export function useFetchGuestSummarizedReviews() {
  const initialParams = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();

  const { reviewsDisabled, aiSummarizedReviewsEnabled } = localization.configValuesApp;

  const key =
    reviewsDisabled || !aiSummarizedReviewsEnabled || !isValidHotelId(initialParams.internalHotelId)
      ? null
      : initialParams.internalHotelId.toString();

  return useSWR<GuestSummarizedReviewsResponse>(
    key,
    async () =>
      fetchWithTracingAndLogging<GuestSummarizedReviewsResponse>(
        `${BASE_PATH}/api/hotel-detail/summarizedHotelReviews`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            internalHotelId: initialParams.internalHotelId,
          }),
        },
        getLoggingInfo(initialParams.pageType, 'static', 'useFetchHotelStatic'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 1000 * 60 * 30,
      shouldRetryOnError: shouldRetryOnServerError,
    }
  );
}
